/* eslint-disable no-param-reassign */

import { HttpsAgent } from "agentkeepalive"
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import axiosRetry, { IAxiosRetryConfig } from "axios-retry"

import {
  authorizeCodeApi,
  AuthorizeCodeRequest,
  AuthorizeCodeResponse,
} from "services/authorize-code"
import { getDeviceIdApi, GetDeviceIdResponse } from "services/deviceId"
import {
  resetPasswordLink,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from "services/forgot-password"
import {
  fetchRboPropertyDataApi,
  FetchRboPropertyDataRequest,
  FetchRboPropertyDataResponse,
} from "services/rbo/fetch-rbo-property-api"
import {
  GetImportCalendarLinkResponse,
  SetCalendarImportLinkRequest,
  setImportCalendarLink,
} from "services/rbo/Import-calendar"
import {
  publishMultiplePropertyForRBOApi,
  PublishMultiplePropertyForRBORequest,
  PublishMultiplePropertyForRBOResponse,
  rboImportListingApi,
  RboImportListingRequest,
  RboImportListingResponse,
  refreshRboPropertiesApi,
  RefreshRboPropertiesRequest,
  RefreshRboPropertiesResponse,
  userGetPropertiesStatusApi,
  UserGetPropertiesStatusRequest,
  UserGetPropertiesStatusResponse,
} from "services/rbo/import-listing"
import {
  reviewProfileApi,
  ReviewProfileRequest,
  ReviewProfileResponse,
  userImportProfileApi,
  UserImportProfileRequest,
  UserImportProfileResponse,
} from "services/rbo/import-profile"
import {
  updateLocationAddressForProperty,
  UpdateLocationAddressRequest,
  UpdateLocationAddressResponse,
} from "services/rbo/location-details-api"
import {
  getCalendarData,
  GetCalendarRequest,
  GetCalendarResponse,
  getCancellationPolicyApi,
  GetCancellationPolicyRequest,
  GetCancellationPolicyResponse,
  getPdpDataApi,
  GetPdpDataResponse,
  publishPropertyForRBO,
  PublishPropertyForRBORequest,
  PublishPropertyForRBOResponse,
  saveCancellationPolicyApi,
  SaveCancellationPolicyRequest,
  SaveCancellationPolicyResponse,
  savePdpDataApi,
  SavePdpDataRequest,
  SavePdpDataResponse,
  updateCalendarAvailabilityDataApi,
  UpdateCalendarAvailabilityRequest,
  UpdateCalendarAvailabilityResponse,
  updateCalendarPricingDataApi,
  UpdateCalendarPricingRequest,
  UpdateCalendarPricingResponse,
} from "services/rbo/review-and-publish"
import {
  updateAvailabilityDataApi,
  UpdateAvailabilityDataRequest,
  UpdateAvailabilityDataResponse,
} from "services/rbo/update-availability-data"
import {
  VerifyResetLinkResponse,
  verifyResetLink,
  VerifyResetLinkRequest,
  newPasswordSetup,
  NewPasswordSetupRequest,
  NewPasswordSetupResponse,
} from "services/reset-password"
import {
  GetAllReviewsRequest,
  GetAllReviewsResponse,
  getAllReviews,
  ReviewReportResponse,
  ReviewReportRequest,
  ReviewReplyResponse,
  ReviewReplyRequest,
  IsUnreadReviewRequest,
  IsUnreadReviewResponse,
  IsMarkReviewReadRequest,
  IsMarkReviewReadResponse,
  reviewReply,
  reviewReport,
  isUnreadReview,
  isMarkReviewRead,
  FilterByPropertyReviewsRequest,
  FilterByPropertyReviewsResponse,
  getFilterByPropertyReviews,
} from "services/reviews"
import {
  deleteQuickReplyMessageApi,
  DeleteQuickReplyMessageRequest,
  DeleteQuickReplyMessageResponse,
} from "services/sendbird/quick-reply-messages/delete-quick-reply-message-api"
import {
  fetchQuickReplyMessageApi,
  FetchQuickReplyMessageRequest,
  FetchQuickReplyMessageResponse,
} from "services/sendbird/quick-reply-messages/fetch-quick-reply-message-api"
import {
  saveQuickReplyMessageApi,
  SaveQuickReplyMessageRequest,
  SaveQuickReplyMessageResponse,
} from "services/sendbird/quick-reply-messages/save-quick-reply-message-api"
import {
  singleQuickReplyMessageApi,
  SingleQuickReplyMessageRequest,
  SingleQuickReplyMessageResponse,
} from "services/sendbird/quick-reply-messages/single-quick-reply-message-api"
import {
  deleteScheduleMessageApi,
  DeleteScheduleMessageRequest,
  DeleteScheduleMessageResponse,
} from "services/sendbird/scheduled-messages/delete-schedule-message-api"
import {
  fetchScheduleMessageApi,
  FetchScheduleMessageRequest,
  FetchScheduleMessageResponse,
} from "services/sendbird/scheduled-messages/fetch-schedule-messages-api"
import {
  saveScheduleMessageApi,
  SaveScheduleMessageRequest,
  SaveScheduleMessageResponse,
} from "services/sendbird/scheduled-messages/save-schedule-message-api"
import {
  getAuthToken,
  getInviteAuth,
  getReCaptchaToken,
  getTempAuthToken,
  removeAuthToken,
  removeReCaptchaToken,
  removeTempAuthToken,
} from "utils/local-storage"

import { customAdapter } from "./adapter"
import {
  getDefaultAmenities,
  AmenitiesResponse,
  addAmenities,
  AmenitiesRequest,
  addCustomAmenities,
  CustomAmenitiesRequest,
  getCustomAmenities,
  GetCustomAmenitiesRequest,
  deleteCustomAmenity,
  DeleteCustomAmenitiesType,
  GetCustomAmenitiesResponse,
} from "./amenities"
import {
  addAttachment,
  AttachmentRequest,
  AttachmentResponse,
  removeAttachment,
  RemoveAttachmentRequest,
  RemoveAttachmentResponse,
  ReorderAttachmentResponse,
  reorderAttachment,
  ReorderAttachmentRequest,
  updateAttachment,
  UpdateAttachmentRequest,
  getAllAttachments,
  GetAllAttachmentsRequest,
  GetAllAttachmentsResponse,
  uploadVideo,
  UploadVideoResponse,
  UploadVideoRequest,
} from "./attachments"
import { AuthRequest, AuthResponse, createPassword, login } from "./auth"
import {
  addRoomDetails,
  AddRoomDetailsRequest,
  AddRoomDetailsResponse,
  DefaultBedsResponse,
  getDefaultBeds,
  getRoomDetails,
  RoomDetailsRequest,
  RoomDetailsResponse,
} from "./bedroom"
import {
  BookingsPropertyResponse,
  fetchSingleBookingDataApi,
  GetBookingPropertyRequest,
} from "./bookings"
import {
  exportCalendar,
  ExportCalendarRequest,
  ExportCalendarResponse,
  importCalendar,
  ImportCalendarRequest,
  ImportCalendarResponse,
} from "./calendar"
import {
  CountryResponse,
  getCountries,
  getCountriesByCode,
  GetCountryByCodeRequest,
} from "./country"
import {
  getErrorLogs,
  GetErrorLogsRequest,
  GetErrorLogsResponse,
} from "./errorLogs"
import { getEvents, GetEventsRequest, GetEventsResponse } from "./events"
import {
  HouseRulesRequest,
  addHouseRules,
  HouseRulesResponse,
} from "./features"
import { FeeCategoriesResponse, getFeeCategories } from "./fee-mapping"
import {
  getTripDetailsApi,
  TripDetailsRequest,
  TripDetailsResponse,
} from "./get-trip-details"
import { getAdminPmc, getHostPmc, HostPmcResponse } from "./host"
import {
  PmcDefaultCheckRequest,
  PmcDefaultCheckResponse,
  pmcDefaultCheck,
  GetAdminCompanyRequest,
  GetAdminCompanyResponse,
  getCompany,
  GetStatesRequest,
  GetStatesResponse,
  getStates,
} from "./liveProperties"
import {
  VerifyOtpRequest,
  VerifyOtpResponse,
  verifyOtp,
  VerifyPasswordRequest,
  verifyPassword,
  VerifyPasswordResponse,
} from "./otp"
import {
  deletePayoutAccount,
  DeletePayoutAccountRequest,
  DeletePayoutAccountResponse,
  IndividualAccountRequest,
  IndividualAccountResponse,
  IndividualBankAccountRequest,
  makeDefaultPayoutAccount,
  MakeDefaultPayoutAccountRequest,
  MakeDefaultPayoutAccountResponse,
  setindividualAccount,
  setindividualBankAccount,
} from "./payout"
import { getPMSLogs, GetPMSLogsRequest, GetPMSLogsResponse } from "./pmslogs"
import {
  addEditUser,
  deleteUser,
  AddEditUserType,
  UserDeleteResponse,
  DeleteUserType,
  AddEditUserByCodeType,
  addEditUserByCode,
  DeleteUserByCodeType,
  deleteUserByCode,
  AcceptTermsConditionsRequest,
  acceptTermsConditions,
  AcceptTermsConditionsResponse,
  AddEditUserResponse,
} from "./profile/manage-users"
import {
  applyPayoutRulesOnProperties,
  ApplyPayoutRulesOnPropertiesRequest,
  ApplyPayoutRulesOnPropertiesResponse,
  GetAccountsByPmcIdRequest,
  GetAccountsByPmcIdResponse,
  getHostAccountList,
  getPayoutProperties,
  PayoutPropertiesRequest,
  PayoutPropertyResponse,
} from "./profile/payout-settings"
import {
  createProperty,
  getPMCDescription,
  GetPmcDescriptionRequest,
  GetPMCDescriptionResponse,
  getProperty,
  GetPropertyRequest,
  GetPropertyResponse,
  hidePropertyImage,
  HidePropertyImageRequest,
  HidePropertyImageResponse,
  PropertUpdateRequest,
  PropertyRequest,
  PropertyResponse,
  PropertyUpdateResponse,
  updateProperty,
  updatePropertyDescription,
} from "./properties"
import {
  getBookingDataForCalendarScreen,
  GetBookingDataForCalendarScreenRequest,
  GetBookingDataForCalendarScreenResponse,
} from "./rbo/fetch-booking-data"
import {
  FetchNextPropertyToPublishRequest,
  FetchNextPropertyToPublishResponse,
  fetchNextPropertyToReview,
} from "./rbo/fetch-next-property-to-review"
import {
  MinAgeRequirementResponse,
  MinAgeRequirementRequest,
  minAgeRequirement,
} from "./rbo/min-age-requirement"
import {
  getOnboardingStatusData,
  GetOnboardingStatusDataRequest,
  GetOnboardingStatusDataResponse,
  updateOnboardingStatusDataApi,
  UpdateOnboardingStatusDataRequest,
} from "./rbo/onboarding-status-data"
import {
  createPasswordSetup,
  CreatePasswordSetupRequest,
  CreatePasswordSetupResponse,
  emailVerifyForSignup,
  saveUserApi,
  SaveUserRequest,
  SaveUserResponse,
  signUpVerifyOtp,
  SignupVerifyOtpRequest,
  VerifyEmailLinkRequest,
  VerifyEmailLinkResponse,
} from "./rbo/signup"
import {
  updatePricing,
  UpdatePricingRequest,
  UpdatePricingResponse,
} from "./rbo/update-pricing"
import {
  videoUpload,
  VideoUploadRequest,
  VideoUploadResponse,
} from "./rbo/video-upload"
import {
  EarningReportsResponse,
  getEarningReportData,
  getPayoutReportData,
  GetPayoutReportRequest,
  getReports,
  GetReportsRequest,
  PayoutReportResponse,
  ReportsResponse,
} from "./reports"
import {
  getUser,
  updateUser,
  ImageUploadRequest,
  ImageUploadResponse,
  uploadProfileImage,
  UserResponse,
  UpdateUserType,
  GetUserRequest,
} from "./user"

export const defaultHttpsAgent = new HttpsAgent({ keepAlive: true })
export const defaultTimeout = 40000

const defaultConfig: AxiosRequestConfig = {
  timeout: defaultTimeout,
  httpsAgent: defaultHttpsAgent,
  adapter: customAdapter,
  headers: {},
}

export const defaultAxiosInstance = axios.create(defaultConfig)
axiosRetry(defaultAxiosInstance)

defaultAxiosInstance.interceptors.request.use(
  (request) => {
    const authToken = getAuthToken()
    const inviteAuthToken = getInviteAuth()
    const tempAuthToken = getTempAuthToken()

    const reCaptchaToken = getReCaptchaToken()
    removeReCaptchaToken()

    if (reCaptchaToken) {
      removeTempAuthToken()
      removeAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          "whimstay-auth": `whimstay ${reCaptchaToken}`,
          app: "rbo",
        },
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if ((request.data?.invCode || request.params?.invCode) && inviteAuthToken) {
      // For manage user by invitation code APIs we need to set different AuthToken.
      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: inviteAuthToken,
          app: "rbo",
        },
      }
    }

    if (tempAuthToken) {
      removeAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: tempAuthToken,
          app: "rbo",
        },
      }
    }

    if (authToken) {
      return {
        ...request,
        headers: { ...request.headers, Authorization: authToken, app: "rbo" },
      }
    }

    return {
      ...request,
      headers: { ...request.headers, app: "rbo" },
    }
  },
  // eslint-disable-next-line promise/no-promise-in-callback
  (error: AxiosError) => Promise.reject(error),
)

defaultAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error?.response?.status === 401) {
      removeAuthToken()
      window.location.href = "/rbo"
    }
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      error?.response?.status === 403 &&
      !window.location.pathname.startsWith("/rbo/signup-rbo") &&
      !window.location.pathname.startsWith("/rbo/verify_email")
    ) {
      window.location.replace(
        `${process.env.REACT_APP_WHIMSTAY_WEB_URL || ""}/rbo/signup-rbo`,
      )
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return Promise.reject(error.response ? error.response.data : error)
  },
)

export type Config = {
  raxConfig?: IAxiosRetryConfig
} & AxiosRequestConfig

export interface ClientOptions {
  axiosInstance?: AxiosInstance
  config?: Config
}

export class Client {
  private axiosInstance: AxiosInstance

  constructor({ axiosInstance, config }: ClientOptions = {}) {
    if (axiosInstance && config) {
      throw new Error("Provide one of axiosInstance or config.")
    }

    if (axiosInstance) {
      this.axiosInstance = axiosInstance
      this.axiosInstance.defaults.headers = {
        ...defaultConfig.headers,
        ...this.axiosInstance.defaults.headers,
      }
    } else if (config) {
      config = { ...defaultConfig, ...config }
      config.headers = { ...defaultConfig.headers, ...(config.headers || {}) }
      this.axiosInstance = axios.create(config)
      axiosRetry(this.axiosInstance)
    } else {
      this.axiosInstance = defaultAxiosInstance
    }
  }

  // logout(request: AuthRequest): Promise<AuthResponse> {
  //   return logout(request, this.axiosInstance)
  // }

  login(request: AuthRequest): Promise<AuthResponse> {
    return login(request, this.axiosInstance)
  }

  verifyOtp(request: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    return verifyOtp(request, this.axiosInstance)
  }

  signUpVerifyOtp(request: SignupVerifyOtpRequest): Promise<VerifyOtpResponse> {
    return signUpVerifyOtp(request, this.axiosInstance)
  }

  verifyPassword(
    request: VerifyPasswordRequest,
  ): Promise<VerifyPasswordResponse> {
    return verifyPassword(request, this.axiosInstance)
  }

  newPasswordSetup(
    request: NewPasswordSetupRequest,
  ): Promise<NewPasswordSetupResponse> {
    return newPasswordSetup(request, this.axiosInstance)
  }

  createPassword(
    request: NewPasswordSetupRequest,
  ): Promise<NewPasswordSetupResponse> {
    return createPassword(request, this.axiosInstance)
  }

  resetPasswordLink(
    request: ResetPasswordRequest,
  ): Promise<ResetPasswordResponse> {
    return resetPasswordLink(request, this.axiosInstance)
  }

  verifyResetLink(
    request: VerifyResetLinkRequest,
  ): Promise<VerifyResetLinkResponse> {
    // const tempInstance = this.axiosInstance
    // if (request.token) {
    //   // Set the Authorization header
    //   tempInstance.defaults.headers.common.authorization = `Bearer ${request.token}`
    // }
    return verifyResetLink(request, this.axiosInstance)
  }

  getUser(request: GetUserRequest): Promise<UserResponse> {
    return getUser(request, this.axiosInstance)
  }

  updateUser(request: UpdateUserType): Promise<UserResponse> {
    return updateUser(request, this.axiosInstance)
  }

  uploadProfileImage(
    request: ImageUploadRequest,
  ): Promise<ImageUploadResponse> {
    return uploadProfileImage(request, this.axiosInstance)
  }

  getHostPmc(): Promise<HostPmcResponse> {
    return getHostPmc(this.axiosInstance)
  }

  getAdminPmc(): Promise<HostPmcResponse> {
    return getAdminPmc(this.axiosInstance)
  }

  getDeviceIdApi(): Promise<GetDeviceIdResponse> {
    return getDeviceIdApi(this.axiosInstance)
  }

  createProperty(request: PropertyRequest): Promise<PropertyResponse> {
    return createProperty(request, this.axiosInstance)
  }

  updateProperty(request: PropertyRequest): Promise<PropertyResponse> {
    return updateProperty(request, this.axiosInstance)
  }

  updatePropertyDescription(
    request: PropertUpdateRequest,
  ): Promise<PropertyUpdateResponse> {
    return updatePropertyDescription(request, this.axiosInstance)
  }

  hidePropertyImage(
    request: HidePropertyImageRequest,
  ): Promise<HidePropertyImageResponse> {
    return hidePropertyImage(request, this.axiosInstance)
  }

  getProperty(request: GetPropertyRequest): Promise<GetPropertyResponse> {
    return getProperty(request, this.axiosInstance)
  }

  getPMCDescription(
    request: GetPmcDescriptionRequest,
  ): Promise<GetPMCDescriptionResponse> {
    return getPMCDescription(request, this.axiosInstance)
  }

  getEvents(request: GetEventsRequest): Promise<GetEventsResponse> {
    return getEvents(request, this.axiosInstance)
  }

  getPMSLogs(request: GetPMSLogsRequest): Promise<GetPMSLogsResponse> {
    return getPMSLogs(request, this.axiosInstance)
  }

  getErrorLogs(request: GetErrorLogsRequest): Promise<GetErrorLogsResponse> {
    return getErrorLogs(request, this.axiosInstance)
  }

  importCalendar(
    request: ImportCalendarRequest,
  ): Promise<ImportCalendarResponse> {
    return importCalendar(request, this.axiosInstance)
  }

  exportCalendar(
    request: ExportCalendarRequest,
  ): Promise<ExportCalendarResponse> {
    return exportCalendar(request, this.axiosInstance)
  }

  getCalendarData(request: GetCalendarRequest): Promise<GetCalendarResponse> {
    return getCalendarData(request, this.axiosInstance)
  }

  getDefaultAmenities(): Promise<AmenitiesResponse> {
    return getDefaultAmenities(this.axiosInstance)
  }

  getCustomAmenities(
    request: GetCustomAmenitiesRequest,
  ): Promise<GetCustomAmenitiesResponse> {
    return getCustomAmenities(request, this.axiosInstance)
  }

  getCountryByCode(request: GetCountryByCodeRequest): Promise<CountryResponse> {
    return getCountriesByCode(request, this.axiosInstance)
  }

  getCountries(): Promise<CountryResponse> {
    return getCountries(this.axiosInstance)
  }

  addAmenities(request: AmenitiesRequest): Promise<AmenitiesResponse> {
    return addAmenities(request, this.axiosInstance)
  }

  addCustomAmenities(
    request: CustomAmenitiesRequest,
  ): Promise<AmenitiesResponse> {
    return addCustomAmenities(request, this.axiosInstance)
  }

  addAttachment(request: AttachmentRequest): Promise<AttachmentResponse> {
    return addAttachment(request, this.axiosInstance)
  }

  updateAttachment(
    request: UpdateAttachmentRequest,
  ): Promise<AttachmentResponse> {
    return updateAttachment(request, this.axiosInstance)
  }

  removeAttachment(
    request: RemoveAttachmentRequest,
  ): Promise<RemoveAttachmentResponse> {
    return removeAttachment(request, this.axiosInstance)
  }

  reorderAttachment(
    request: ReorderAttachmentRequest,
  ): Promise<ReorderAttachmentResponse> {
    return reorderAttachment(request, this.axiosInstance)
  }

  addHouseRules(request: HouseRulesRequest): Promise<HouseRulesResponse> {
    return addHouseRules(request, this.axiosInstance)
  }

  getFeeCategories(): Promise<FeeCategoriesResponse> {
    return getFeeCategories(this.axiosInstance)
  }

  setindividualAccount(
    request: IndividualAccountRequest,
  ): Promise<IndividualAccountResponse> {
    return setindividualAccount(request, this.axiosInstance)
  }

  setindividualBankAccount(
    request: IndividualBankAccountRequest,
  ): Promise<IndividualAccountResponse> {
    return setindividualBankAccount(request, this.axiosInstance)
  }

  getAllAttachments(
    request: GetAllAttachmentsRequest,
  ): Promise<GetAllAttachmentsResponse> {
    return getAllAttachments(request, this.axiosInstance)
  }

  getDefaultBeds(): Promise<DefaultBedsResponse> {
    return getDefaultBeds(this.axiosInstance)
  }

  getRoomDetails(request: RoomDetailsRequest): Promise<RoomDetailsResponse> {
    return getRoomDetails(request, this.axiosInstance)
  }

  addRoomDetails(
    request: AddRoomDetailsRequest,
  ): Promise<AddRoomDetailsResponse> {
    return addRoomDetails(request, this.axiosInstance)
  }

  fetchBookingData(
    request: GetBookingPropertyRequest,
  ): Promise<BookingsPropertyResponse> {
    return fetchSingleBookingDataApi(request, this.axiosInstance)
  }

  getTripDetailsApi(request: TripDetailsRequest): Promise<TripDetailsResponse> {
    return getTripDetailsApi(request, this.axiosInstance)
  }

  addEditUser(request: AddEditUserType): Promise<AddEditUserResponse> {
    return addEditUser(request, this.axiosInstance)
  }

  deleteUser(request: DeleteUserType): Promise<UserDeleteResponse> {
    return deleteUser(request, this.axiosInstance)
  }

  deleteCustomAmenity(
    request: DeleteCustomAmenitiesType,
  ): Promise<AmenitiesResponse> {
    return deleteCustomAmenity(request, this.axiosInstance)
  }

  getHostAccountList(
    request: GetAccountsByPmcIdRequest,
  ): Promise<GetAccountsByPmcIdResponse> {
    return getHostAccountList(request, this.axiosInstance)
  }

  getPayoutProperties(
    request: PayoutPropertiesRequest,
  ): Promise<PayoutPropertyResponse> {
    return getPayoutProperties(request, this.axiosInstance)
  }

  applyPayoutRulesOnProperties(
    request: ApplyPayoutRulesOnPropertiesRequest,
  ): Promise<ApplyPayoutRulesOnPropertiesResponse> {
    return applyPayoutRulesOnProperties(request, this.axiosInstance)
  }

  getReports(request: GetReportsRequest): Promise<ReportsResponse> {
    const tempInstance = this.axiosInstance
    if (!(request.params.format === "JSON")) {
      tempInstance.interceptors.request.use((req) => ({
        ...req,
        timeout: 180000,
      }))
    }
    return getReports(request, tempInstance)
  }

  getEarningReportData(
    request: GetReportsRequest,
  ): Promise<EarningReportsResponse> {
    const tempInstance = this.axiosInstance
    if (!(request.params.format === "JSON")) {
      tempInstance.interceptors.request.use((req) => ({
        ...req,
        timeout: 180000,
      }))
    }
    return getEarningReportData(request, tempInstance)
  }

  getPayoutReportData(
    request: GetPayoutReportRequest,
  ): Promise<PayoutReportResponse> {
    const tempInstance = this.axiosInstance
    if (!(request.params.format === "JSON")) {
      tempInstance.interceptors.request.use((req) => ({
        ...req,
        timeout: 180000,
      }))
    }
    return getPayoutReportData(request, tempInstance)
  }

  uploadVideo(request: UploadVideoRequest): Promise<UploadVideoResponse> {
    return uploadVideo(request, this.axiosInstance)
  }

  authorizeCodeApi(
    request: AuthorizeCodeRequest,
  ): Promise<AuthorizeCodeResponse> {
    return authorizeCodeApi(request, this.axiosInstance)
  }

  // readNotification(request: ReadNotificationRequest): Promise<unknown> {
  //   return readNotification(request, this.axiosInstance)
  // }

  addEditUserByCode(
    request: AddEditUserByCodeType,
  ): Promise<AddEditUserResponse> {
    return addEditUserByCode(request, this.axiosInstance)
  }

  deleteUserByCode(request: DeleteUserByCodeType): Promise<UserDeleteResponse> {
    return deleteUserByCode(request, this.axiosInstance)
  }

  acceptTermsConditions(
    request: AcceptTermsConditionsRequest,
  ): Promise<AcceptTermsConditionsResponse> {
    return acceptTermsConditions(request, this.axiosInstance)
  }

  deletePayoutAccount(
    request: DeletePayoutAccountRequest,
  ): Promise<DeletePayoutAccountResponse> {
    return deletePayoutAccount(request, this.axiosInstance)
  }

  makeDefaultPayoutAccount(
    request: MakeDefaultPayoutAccountRequest,
  ): Promise<MakeDefaultPayoutAccountResponse> {
    return makeDefaultPayoutAccount(request, this.axiosInstance)
  }

  pmcDefaultCheck(
    request: PmcDefaultCheckRequest,
  ): Promise<PmcDefaultCheckResponse> {
    return pmcDefaultCheck(request, this.axiosInstance)
  }

  // -------- signup api call starts ----------
  saveUser(request: SaveUserRequest): Promise<SaveUserResponse> {
    return saveUserApi(request, this.axiosInstance)
  }

  emailVerifyForSignup(
    request: VerifyEmailLinkRequest,
  ): Promise<VerifyEmailLinkResponse> {
    return emailVerifyForSignup(request, this.axiosInstance)
  }

  createPasswordSetup(
    request: CreatePasswordSetupRequest,
  ): Promise<CreatePasswordSetupResponse> {
    return createPasswordSetup(request, this.axiosInstance)
  }

  // ------------- user import profile api for property management flow (step 3) -----------
  userImportProfile(
    request: UserImportProfileRequest,
  ): Promise<UserImportProfileResponse> {
    return userImportProfileApi(request, this.axiosInstance)
  }

  // ----- review profile api for property management flow (step 4) -----------
  reviewProfile(request: ReviewProfileRequest): Promise<ReviewProfileResponse> {
    return reviewProfileApi(request, this.axiosInstance)
  }

  // -------- rbo import listing api call-----
  rboImportListing(
    request: RboImportListingRequest,
  ): Promise<RboImportListingResponse> {
    return rboImportListingApi(request, this.axiosInstance)
  }

  // -------- refresh & sync rbo properties data api call -----
  refreshRboProperties(
    request: RefreshRboPropertiesRequest,
  ): Promise<RefreshRboPropertiesResponse> {
    return refreshRboPropertiesApi(request, this.axiosInstance)
  }

  // -------- user get property status [import listing screen ] ----------
  userGetPropertiesStatus(
    request: UserGetPropertiesStatusRequest,
  ): Promise<UserGetPropertiesStatusResponse> {
    return userGetPropertiesStatusApi(request, this.axiosInstance)
  }
  // -------- user get property status [import listing screen ] end ----------

  // -------- publish multiple property for import listing screen --------
  publishMultiplePropertyForRBO(
    request: PublishMultiplePropertyForRBORequest,
  ): Promise<PublishMultiplePropertyForRBOResponse> {
    return publishMultiplePropertyForRBOApi(request, this.axiosInstance)
  }
  // -------- publish multiple property for import listing screen end --------

  getCompany(
    request: GetAdminCompanyRequest,
  ): Promise<GetAdminCompanyResponse> {
    return getCompany(request, this.axiosInstance)
  }

  getStates(request: GetStatesRequest): Promise<GetStatesResponse> {
    return getStates(request, this.axiosInstance)
  }

  // --------  update location address for property location details screen --------
  updateLocationAddressForProperty(
    request: UpdateLocationAddressRequest,
  ): Promise<UpdateLocationAddressResponse> {
    return updateLocationAddressForProperty(request, this.axiosInstance)
  }

  // -------- import calendar link [availability tab in review & publish screen] --------
  setImportCalendarLink(
    request: SetCalendarImportLinkRequest,
  ): Promise<GetImportCalendarLinkResponse> {
    return setImportCalendarLink(request, this.axiosInstance)
  }

  // --------  publish property for rbo review and publish screen --------
  publishPropertyForRBO(
    request: PublishPropertyForRBORequest,
  ): Promise<PublishPropertyForRBOResponse> {
    return publishPropertyForRBO(request, this.axiosInstance)
  }

  // ----- fetch rbo properties data api call [review & publish screen]-----
  fetchRboPropertyData(
    request: FetchRboPropertyDataRequest,
  ): Promise<FetchRboPropertyDataResponse> {
    return fetchRboPropertyDataApi(request, this.axiosInstance)
  }

  // ------- get cancellation policy data [Policies & Rules tab in review & publish screen] -----
  getCancellationPolicy(
    request: GetCancellationPolicyRequest,
  ): Promise<GetCancellationPolicyResponse> {
    return getCancellationPolicyApi(request, this.axiosInstance)
  }

  // ------- save cancellation policy data [Policies & Rules tab in review & publish screen] -----
  saveCancellationPolicy(
    request: SaveCancellationPolicyRequest,
  ): Promise<SaveCancellationPolicyResponse> {
    return saveCancellationPolicyApi(request, this.axiosInstance)
  }

  // ------- get pdp data [Policies & Rules tab in review & publish screen] -----
  getPdpData(
    request: GetCancellationPolicyRequest,
  ): Promise<GetPdpDataResponse> {
    return getPdpDataApi(request, this.axiosInstance)
  }

  // ------- save pdp data [Policies & Rules tab in review & publish screen] -----
  savePdpData(request: SavePdpDataRequest): Promise<SavePdpDataResponse> {
    return savePdpDataApi(request, this.axiosInstance)
  }

  // ------- update calendar pricing data [review & publish screen] -----
  updateCalendarPricingData(
    request: UpdateCalendarPricingRequest,
  ): Promise<UpdateCalendarPricingResponse> {
    return updateCalendarPricingDataApi(request, this.axiosInstance)
  }

  // ------- update calendar availability data [review & publish screen] -----
  updateCalendarAvailabilityData(
    request: UpdateCalendarAvailabilityRequest,
  ): Promise<UpdateCalendarAvailabilityResponse> {
    return updateCalendarAvailabilityDataApi(request, this.axiosInstance)
  }

  // ------- upload video [Add a Video to Your Listing] -----
  videoUpload(request: VideoUploadRequest): Promise<VideoUploadResponse> {
    return videoUpload(request, this.axiosInstance)
  }

  getAllReviews(request: GetAllReviewsRequest): Promise<GetAllReviewsResponse> {
    return getAllReviews(request, this.axiosInstance)
  }

  reviewReply(request: ReviewReplyRequest): Promise<ReviewReplyResponse> {
    return reviewReply(request, this.axiosInstance)
  }

  reviewReport(request: ReviewReportRequest): Promise<ReviewReportResponse> {
    return reviewReport(request, this.axiosInstance)
  }

  isUnreadReview(
    request: IsUnreadReviewRequest,
  ): Promise<IsUnreadReviewResponse> {
    return isUnreadReview(request, this.axiosInstance)
  }

  isMarkReviewRead(
    request: IsMarkReviewReadRequest,
  ): Promise<IsMarkReviewReadResponse> {
    return isMarkReviewRead(request, this.axiosInstance)
  }

  getFilterByPropertyReviews(
    request: FilterByPropertyReviewsRequest,
  ): Promise<FilterByPropertyReviewsResponse> {
    return getFilterByPropertyReviews(request, this.axiosInstance)
  }

  // --------  minimum age requirement [ listing data step ] - rbo --------
  minAgeRequirement(
    request: MinAgeRequirementRequest,
  ): Promise<MinAgeRequirementResponse> {
    return minAgeRequirement(request, this.axiosInstance)
  }

  // --------  Get onboarding status data - rbo --------
  getOnboardingStatusData(
    request: GetOnboardingStatusDataRequest,
  ): Promise<GetOnboardingStatusDataResponse> {
    return getOnboardingStatusData(request, this.axiosInstance)
  }

  // ----------- update onBoarding status data - rbo -----------
  updateOnboardingStatusData(
    request: UpdateOnboardingStatusDataRequest,
  ): Promise<GetOnboardingStatusDataResponse> {
    return updateOnboardingStatusDataApi(request, this.axiosInstance)
  }

  // --------  update pricing [ listing data step ] - rbo --------
  updatePricing(request: UpdatePricingRequest): Promise<UpdatePricingResponse> {
    return updatePricing(request, this.axiosInstance)
  }

  // --------  update availability data [ listing data step ] - rbo --------
  updateAvailabilityData(
    request: UpdateAvailabilityDataRequest,
  ): Promise<UpdateAvailabilityDataResponse> {
    return updateAvailabilityDataApi(request, this.axiosInstance)
  }

  // ------- schedule message all api call start here-----
  // ------- fetch schedule message data api -------
  fetchScheduleMessage(
    request: FetchScheduleMessageRequest,
  ): Promise<FetchScheduleMessageResponse> {
    return fetchScheduleMessageApi(request, this.axiosInstance)
  }

  // ------- save schedule message data api -------
  saveScheduleMessage(
    request: SaveScheduleMessageRequest,
  ): Promise<SaveScheduleMessageResponse> {
    return saveScheduleMessageApi(request, this.axiosInstance)
  }

  // ------- delete schedule message data api -------
  deleteScheduleMessage(
    request: DeleteScheduleMessageRequest,
  ): Promise<DeleteScheduleMessageResponse> {
    return deleteScheduleMessageApi(request, this.axiosInstance)
  }
  // ------- schedule message all api call end here -----

  // ------- quick reply message all api call start here -----
  // ------- fetch quick reply message data api -------
  fetchQuickReplyMessage(
    request: FetchQuickReplyMessageRequest,
  ): Promise<FetchQuickReplyMessageResponse> {
    return fetchQuickReplyMessageApi(request, this.axiosInstance)
  }

  // ------- save quick reply message data api -------
  saveQuickReplyMessage(
    request: SaveQuickReplyMessageRequest,
  ): Promise<SaveQuickReplyMessageResponse> {
    return saveQuickReplyMessageApi(request, this.axiosInstance)
  }

  // ------ single quick reply message data api -------
  singleQuickReplyMessage(
    request: SingleQuickReplyMessageRequest,
  ): Promise<SingleQuickReplyMessageResponse> {
    return singleQuickReplyMessageApi(request, this.axiosInstance)
  }

  // ------- delete quick reply message data api -------
  deleteQuickReplyMessage(
    request: DeleteQuickReplyMessageRequest,
  ): Promise<DeleteQuickReplyMessageResponse> {
    return deleteQuickReplyMessageApi(request, this.axiosInstance)
  }
  // ------- quick reply message all api call end here -----

  // ------- fetch next property to review --------
  fetchNextPropertyToReview(
    request: FetchNextPropertyToPublishRequest,
  ): Promise<FetchNextPropertyToPublishResponse> {
    return fetchNextPropertyToReview(request, this.axiosInstance)
  }
  // ------- fetch next property to review --------

  getBookingDataForCalendarScreen(
    request: GetBookingDataForCalendarScreenRequest,
  ): Promise<GetBookingDataForCalendarScreenResponse> {
    return getBookingDataForCalendarScreen(request, this.axiosInstance)
  }
}

export const apiClient = new Client()
